// src/modules/app-views/ticket-events/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useMemo, useState } from 'react';
import { Tabs } from 'antd';
import { BarChartOutlined, FormOutlined, ShoppingOutlined } from '@ant-design/icons';
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import { useDocumentContext } from 'components/core/DocumentProvider';
import useZustand from 'hooks/useZustand';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import translateX from 'utils/translateX';
import dayjs from 'dayjs';
import axiosService from 'services/axiosService';
import { API, apiUrl } from 'constants/Api';
import TicketEventsRecordTab from './tabs/RecordTab';
import TicketEventsDashboardTab from './tabs/DashboardTab';
import TicketEventsSalesTab from './tabs/SalesTab';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const TicketEventsDocument = props => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode } =
    useDocumentContext();

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef, excludeFieldsFromReset } = useFormInstance({ data });

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const requiredFields = ['name', 'totalTickets', 'minimumPrice', 'maximumPrice', 'salePeriod'];
  const isNewDocument = viewMode === 'add';

  const tabsItems = useMemo(() => {
    return Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
  }, [availableTabs]);

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);
  const [disabledFields, setDisabledFields] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!initTab) return;
  }, [initTab]);

  useEffect(() => {
    if (!data?._id || data._id === 'add') return;
    fetchTotalRevenue(data._id);
  }, [data?._id]);

  useEffect(() => {
    const childProps = { ...props, initTab, viewMode, data };
    const itemsLocal = buildTabsArray(childProps, isNewDocument);
    buildTabs(itemsLocal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, viewMode, initTab, isNewDocument]);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const fetchTotalRevenue = async eventId => {
    try {
      const url = apiUrl.build({
        url: API.TICKET_SALES.STATS,
        params: { eventId },
      });
      const [statsData] = await axiosService.get({ url, returnAsObject: true });
      setTotalRevenue(statsData?.sales?.totalRevenue ?? 0);
    } catch (_) {
      setTotalRevenue(0);
    }
  };

  const handleFinish = async ({ values, allValues }) => {
    const payload = { ...values };

    // Convert salePeriod RangePicker to saleStartDate + saleEndDate
    if (Array.isArray(payload.salePeriod) && payload.salePeriod.length === 2) {
      payload.saleStartDate = payload.salePeriod[0]?.toISOString?.() || payload.salePeriod[0];
      payload.saleEndDate = payload.salePeriod[1]?.toISOString?.() || payload.salePeriod[1];
      delete payload.salePeriod;
    }

    // Convert eventDate dayjs to ISO string
    if (payload.eventDate && typeof payload.eventDate === 'object' && payload.eventDate.toISOString) {
      payload.eventDate = payload.eventDate.toISOString();
    }

    const _id = viewMode !== 'add' ? urlInfo?.params?._id : undefined;
    await upsert({ dataSet: payload, _id });
  };

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // Convert date strings to dayjs before the form receives data, and inject _totalRevenue
  const formData = useMemo(() => {
    if (!data) return data;
    const result = { ...data };
    if (result.saleStartDate && result.saleEndDate) {
      result.salePeriod = [dayjs(result.saleStartDate), dayjs(result.saleEndDate)];
    }
    if (result.eventDate && typeof result.eventDate === 'string') {
      result.eventDate = dayjs(result.eventDate);
    }
    result._totalRevenue = totalRevenue;
    return result;
  }, [data, totalRevenue]);

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <div className="container mt-3">
      <SimpleFormProvider
        formRef={formRef}
        onFinish={handleFinish}
        showSubmitButton={false}
        initFocus="name"
        reloadEntirePage={true}
        ignoreResetFormOnSubmit={true}
        requiredFields={requiredFields}
        disabledFields={disabledFields}
        locked={viewMode === 'view'}
        showInfoMessage={true}
        initialValues={initialValues}
        data={formData}
      >
        <SimpleFormProviderInitializer data={formData} isInitializedRef={isInitializedRef} />

        <CardHeaderWithCountry data={data} />

        <SimpleLoadingArea name="ticketEventsDocument" />

        <Tabs
          type="line"
          activeKey={tab}
          onChange={handleTabChange}
          items={tabsItems}
          rootClassName="tab-in-form no-content-padding"
          animated={{ inkBar: true, tabPane: false }}
          destroyOnHidden={false}
        />
      </SimpleFormProvider>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TicketEventsDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************

function buildTabsArray(props, isNewDocument) {
  const { initTab } = props;

  return [
    {
      key: initTab,
      label: (
        <span className="tab-icon-label">
          <FormOutlined />
          {translateX('record')}
        </span>
      ),
      hide: false,
      disabled: false,
      children: <TicketEventsRecordTab />,
    },
    {
      key: 'dashboard',
      label: (
        <span className="tab-icon-label">
          <BarChartOutlined />
          {translateX('dashboard')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <TicketEventsDashboardTab />,
    },
    {
      key: 'sales',
      label: (
        <span className="tab-icon-label">
          <ShoppingOutlined />
          {translateX('sales')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <TicketEventsSalesTab />,
    },
  ];
}
